import { DEFAULT_TOKEN, getPageSize } from 'services/PaginationHelper';
import { ruleTriggerDescription, ruleFields } from 'services/utils';
import { UPDATE_DEVICE } from 'services/Permissions';
import { States } from '../../../app.router';


const PAGE_SIZE_SUFFIX = 'rules';

/* @ngInject */
export default class RulesController {
    /**
     * @param $state
     * @param $scope
     * @param RoleManager
     * @param {RulesService} RulesService
     * @param {DialogService} DialogService
     * @param {ToastService} ToastService
     * @param {SensorService} SensorService
     */
    constructor($state, $scope, Loader, RoleManager, RulesService, DialogService, ToastService, SensorService, AnalyticsService, UserPreferencesManager, IAMService, ProjectManager) {
        this.$state = $state;
        this.$scope = $scope;
        this.Loader = Loader;
        this.RoleManager = RoleManager;
        this.RulesService = RulesService;
        this.DialogService = DialogService;
        this.ToastService = ToastService;
        this.SensorService = SensorService;
        this.AnalyticsService = AnalyticsService;
        this.UserPreferencesManager = UserPreferencesManager;
        this.IAMService = IAMService;
        this.ProjectManager = ProjectManager;
    }

    $onInit() {
        this.pageState = States.ALERTS_RULES;
        this.rules = [];
        this.loadingRules = true;
        this.contactGroups = []; // For organization templates
        this.Loader.promise = this.RulesService.listRules()
            .then(response => {
                this.rules = response.data;
                this.rules.forEach(rule => {
                    this.countFieldSwitcheroo(rule) // Change field name for count based notifications
                });

                // Load contact groups if there are organization templates
                const hasOrganizationTemplates = this.rules.some(rule => this.isOrganizationTemplate(rule));
                if (hasOrganizationTemplates) {
                    this.loadContactGroups();
                }

                this.loadingRules = false;
            });
        this.currentPageSize = getPageSize(PAGE_SIZE_SUFFIX);
        this.currentPageToken = DEFAULT_TOKEN;
        this.nextPageToken = null;
        this.requestParams = {
            pageSize: this.currentPageSize,
            pageToken: this.currentPageToken
        };

        this.streamSubscription = this.SensorService.subscribeToAllUpdates({}, (event) => {
            this.$scope.$broadcast('deviceUpdate', event);
        });
        this.fields = ruleFields;
    }

    $onDestroy() {
        if (this.streamSubscription) {
            this.streamSubscription.stop();
        }
    }

    get isNewRule() {
        return this.$state.params.ruleId === 'new';
    }

    get isDetailView() {
        return this.$state.is(States.ALERTS_RULES_DETAIL);
    }

    get canCreate() {
        // TODO: Replace 'UPDATE_DEVICE' with a rules specific permission.
        return this.RoleManager.can(UPDATE_DEVICE);
    }

    get canDelete() {
        // TODO: Replace 'UPDATE_DEVICE' with a rules specific permission.
        return this.RoleManager.can(UPDATE_DEVICE);
    }

    ruleTriggerDescription(rule) {
        rule.triggered = {
            trigger: rule.trigger,
        }
        return ruleTriggerDescription(rule)
    }

    isActive(id) {
        return this.$state.params.ruleId === id;
    }

    onRuleCreated({ rule }) {
        this.countFieldSwitcheroo(rule) // Change field name for count based notifications
        this.rules.unshift(rule);

        this.AnalyticsService.trackEvent("notifications.created")
    }

    onRuleUpdated({ rule }) {
        this.countFieldSwitcheroo(rule) // Change field name for count based notifications
        const ref = this.rules.find(i => i.name === rule.name);
        Object.assign(ref, rule);

        this.AnalyticsService.trackEvent("notifications.updated")
    }

    onRuleDeleted({ ruleId }) {
        this.rules = this.rules.filter(i => i.id !== ruleId);
        this.AnalyticsService.trackEvent("notifications.deleted")
    }

    // Convert field name for count based notifications (locally in Studio)
    // Converted back to the normal field names when communicating with the backend
    countFieldSwitcheroo(rule) { // eslint-disable-line class-methods-use-this
        if (rule.trigger.field === 'objectPresent' && rule.trigger.triggerCount) {
            rule.trigger.field = 'proximityCount'
        }
        if (rule.trigger.field === 'touch' && rule.trigger.triggerCount) {
            rule.trigger.field = 'touchCount'
        }
        if (rule.trigger.field === 'contact' && rule.trigger.triggerCount) {
            rule.trigger.field = 'contactCount'
        }
    }

    showDeleteConfirmation({ rule }) {
        this.DialogService.confirm({
            title: 'Delete Alert Rule',
            textContent: `Do you really want to delete the alert rule "${rule.displayName}"?`,
            ok: 'Delete',
            cancel: 'Cancel'
        })
            .then(() => {
                this.RulesService.deleteRule(rule.id)
                    .then(() => {
                        this.onRuleDeleted({ ruleId: rule.id });
                        this.ToastService.showSimpleTranslated('alert_rule_was_deleted');
                    })
                    .catch(() => {
                        this.ToastService.showSimpleTranslated('alert_rule_wasnt_deleted');
                    });
            })
            .catch(() => false);
    }

    // Check if a rule is from an organization template
    isOrganizationTemplate(rule) {
        return rule && rule.name && rule.name.startsWith('organizations/');
    }

    getTotalRecipientsCount(action) {
        // Map action types to their corresponding property names
        const actionTypeMap = {
            'EMAIL': 'email',
            'SMS': 'sms',
            'PHONE_CALL': 'phoneCall'
        };

        const propertyName = actionTypeMap[action.type];
        if (!propertyName) return 0;

        const actionObject = action[propertyName];
        if (!actionObject) return 0;

        const recipientsCount = actionObject.recipients ? actionObject.recipients.length : 0;
        const contactGroupsCount = actionObject.contactGroups ? actionObject.contactGroups.length : 0;

        return recipientsCount + contactGroupsCount;
    }

    getContactGroupDisplayName(resourceName) {
        // For organization templates viewed at project level, we need to get contact groups
        // This will be populated when viewing organization templates
        if (this.contactGroups) {
            const contactGroup = this.contactGroups.find(cg => cg.name === resourceName);
            return contactGroup ? contactGroup.displayName : resourceName;
        }
        return resourceName;
    }

    async loadContactGroups() {
        // Load contact groups for organization templates
        if (this.ProjectManager?.currentProject?.organizationId && this.IAMService) {
            try {
                const organizationId = this.ProjectManager.currentProject.organizationId;
                const response = await this.IAMService.listContactGroups(organizationId);
                this.contactGroups = response.contactGroups || [];
                this.$scope.$applyAsync();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load contact groups for rules list:', error);
                this.contactGroups = [];
            }
        }
    }
}
